import { Fragment, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Container, Divider, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../routing/LangRouter";
import { connect } from "react-redux";

//import utils
import { fixNumbers, scrollToPosition } from "../../utils/utility";
import { formatPersianDate } from "../../utils/date";
import { flightDataMapper } from "../../utils/flightDataMapper";
import { passengersToPersonCounter } from "../../utils/passengersToPersonCounter";

//import constants
import { IS_MOBILE } from "../../constants/breakpoints";

//import general component
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import BaseTitle from "../../components/common/base-title/base-title";
import { BaseButton } from "../../components/common/base-button/base-button";
import OldPassengersModal from "../../components/view/passengers-forms/old-passengers-modal";
import TabPanel from "../../components/common/base-tab/base-tabs-panel";
import BaseTabPanel from "../../components/common/base-tab/base-tab-panel";
import SelectedItem from "../../components/common/selected-item/selected-item";

//import view components
import PassengersFormContainer from "../../components/view/passengers-forms/passengers-form-container";
import RulesLink from "../rules/rules-link";

//import images and icons
import Icon from "../../components/common/base-icon/icon";

//import styles
import "./passengers.scss";

export default connect()(function FlightPassengers({ dispatch }) {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const accessToken = localStorage.getItem("accessToken");
    // const [isLoginOpen, setIsLoginOpen] = useState(false);

    //sessionStorage variables
    const { isItinerary, isAutomaticReserve } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation")) || {};
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight"));
    const flightPassengers = JSON.parse(sessionStorage.getItem("flightPassengers"));
    const selectedRooms = JSON.parse(sessionStorage.getItem("selectedRooms"));
    const selectedIntRooms = JSON.parse(sessionStorage.getItem("selectedIntRooms"));
    const selectedHotel = JSON.parse(sessionStorage.getItem("selectedHotel"));
    const selectedReservation = JSON.parse(sessionStorage.getItem("selectedReservation")) || {};
    const { item } = selectedReservation;
    const isTwoWay = domesticFlightInformation?.returnDate;

    //flight data variables
    const normalizedDepartureFlight = flightDataMapper(isTwoWay, selectedFlight, selectedFlight?.segments);
    const normalizedReturnFlight = flightDataMapper(isTwoWay, selectedFlight, selectedFlight?.return_segments);

    //generate an array of passenger types for the automatic reservation
    const { personCounter } = passengersToPersonCounter?.(item?.passengers, selectedReservation);

    //generate an array of passenger types
    const passengerTypes = () => {
        const { adult, child, infant } = domesticFlightInformation?.personCounter || personCounter;
        const passengerCount = { adult, child, infant };
        const passengerTypesArray = [];

        Object.entries(passengerCount).forEach(([type, count]) => {
            for (let i = 0; i < parseInt(count); i++) {
                passengerTypesArray.push(type);
            }
        });

        return passengerTypesArray;
    };

    //passenger type code
    const PASSENGER_TYPE_CODES = {
        adult: "ADL",
        child: "CHD",
        infant: "INF",
    };

    useEffect(() => {
        scrollToPosition();
    }, []);

    //initial value for forms
    const defaultValues = flightPassengers ?? {
        passengers: passengerTypes()?.map(() => {
            return {
                first_name: "",
                last_name: "",
                english_first_name: "",
                english_last_name: "",
                phone_number: "",
                national_code: "",
                passport_no: "",
                passport_country: "",
                passport_exp_date: "",
                nationality: "",
                gender: "",
                passenger_type: "",
                birthdate: "",
                email: "",
            };
        }),
    };

    const {
        control,
        register,
        handleSubmit,
        watch,
        trigger,
        setValue,
        getValues,
        resetField,
        clearErrors,
        formState: { errors },
    } = useForm({
        defaultValues: defaultValues,
        mode: "all",
        delayError: 500,
    });

    const { fields: forms } = useFieldArray({
        control,
        name: "passengers",
    });

    const handleRegistration = async data => {
        if (IS_MOBILE) {
            // Create array of required fields
            const requiredFields = [
                "english_first_name",
                "english_last_name",
                ...(!isAutomaticReserve ? ["phone_number"] : []),
                "nationality",
                "gender",
                "birthdate",
            ];

            // Validate each passenger form
            const isValid = data.passengers.every((formData, index) => {
                const idField = IDcard[index]?.nationalCode ? "national_code" : "passport_no";
                const allFields = [...requiredFields, idField];

                const isFormValid = allFields.every(field => formData[field]);

                if (!isFormValid) {
                    setTabValue(index);
                    toast.error(t("Toast Messages.Complete Forms for Each Passenger"));
                    return false;
                }
                return true;
            });

            if (!isValid) return;
        }

        // Convert Persian numbers to English in passenger data
        const convertedData = {
            ...data,
            passengers: data.passengers.map(passenger => ({
                ...passenger,
                email: fixNumbers(passenger.email),
                phone_number: fixNumbers(passenger.phone_number),
                national_code: fixNumbers(passenger.national_code),
                passport_no: fixNumbers(passenger.passport_no),
            })),
        };

        if (!!accessToken) {
            sessionStorage.setItem("flightPassengers", JSON.stringify(convertedData));
            if (isItinerary) {
                if (selectedRooms && selectedHotel) {
                    navigate(`/${locale}/hotel-passengers`);
                } else if (selectedIntRooms && selectedHotel) {
                    navigate(`/${locale}/int-hotel-passengers`);
                } else {
                    navigate(`/${locale}/confirm-information`);
                }
            } else if (isAutomaticReserve) {
                navigate(`/${locale}/flight-automatic-reserve/confirm`);
            } else {
                navigate(`/${locale}/confirm-information`);
            }
        } else {
            setSearchParams({ action: "login" });
        }
    };

    const handleError = error => {
        if (IS_MOBILE) {
            trigger();
            const passengerErrors = error?.passengers;
            const firstPassengerErrorIndex = passengerErrors.findIndex(item => item);
            setTabValue(firstPassengerErrorIndex);
        }
    };

    //value for id and rate plan id rooms
    useEffect(() => {
        setTimeout(() => {
            passengerTypes().forEach((type, index) => {
                setValue(`passengers.${index}.passenger_type`, PASSENGER_TYPE_CODES[type]);
            });
        }, 1000);
    }, []);

    //handel the selection of passenger from previous passenger
    const [IDcard, setIDcard] = useState([]);

    const [isPassengersModalOpen, setIsPassengersModalOpen] = useState(false);
    const handleSelectPassenger = (passenger, index) => {
        clearErrors("passengers");
        setValue(
            `passengers.${index}`,
            {
                passenger_type: PASSENGER_TYPE_CODES[passengerTypes()[index]],
                first_name: IDcard[index].nationalCode ? passenger.first_name : null,
                last_name: IDcard[index].nationalCode ? passenger.last_name : null,
                english_first_name: passenger.english_first_name,
                english_last_name: passenger.english_last_name,
                phone_number: passenger.phone_number,
                nationality: passenger.nationality,
                gender: passenger.gender,
                email: passenger.email,
                birthdate: passenger.birthdate,
                national_code: IDcard[index].nationalCode ? passenger.national_code : null,
                passport_no: IDcard[index].passport ? passenger.passport_no : null,
                passport_country: IDcard[index].passport ? passenger.passport_issue_country : null,
                passport_exp_date: IDcard[index].passport ? passenger.passport_expiry_date : null,
            },
            { shouldValidate: true },
        );
    };

    //handle modals
    useEffect(() => {
        if (searchParams.get("action") === "old-passengers") setIsPassengersModalOpen(true);
        else setIsPassengersModalOpen(false);
    }, [searchParams]);

    //handle form tabs on mobile
    const [tabValue, setTabValue] = useState(0);
    const handleTabChange = (event, newValue) => {
        if (!errors.passengers?.[tabValue]) {
            scrollToPosition(0, 0);
            setTabValue(newValue);
        }
    };

    //handle navigate back on Mobile
    const navigateBack = () => {
        if (isItinerary) navigate(`/${locale}/cart`, { replace: true });
        else navigate({ pathname: `/${locale}/domestic-flight` }, { replace: true });
    };

    const data = {
        title: `${t("departureTicket")}  ${!!isTwoWay ? t("Flight.And Return") : ""}`,
        name: [domesticFlightInformation?.departure?.name, domesticFlightInformation?.destination?.name],
        date: (
            <Grid container item xs={12} direction="column">
                <span>
                    {t("departureDate")}:{" "}
                    {formatPersianDate(normalizedDepartureFlight?.departureDate, {
                        day: "numeric",
                        month: "long",
                        year: undefined,
                        weekday: "long",
                    })}{" "}
                    - {normalizedDepartureFlight?.departureTime?.split(":", 2).join(":")}
                </span>
                {!!isTwoWay && (
                    <span>
                        {t("returnDate")}:{" "}
                        {formatPersianDate(normalizedReturnFlight?.departureDate, {
                            day: "numeric",
                            month: "long",
                            year: undefined,
                            weekday: "long",
                        })}{" "}
                        - {normalizedReturnFlight?.departureTime?.split(":", 2).join(":")}
                    </span>
                )}
            </Grid>
        ),
        totalPrice: selectedFlight?.fares?.total_price,
        type: "flight",
        details: { departure: normalizedDepartureFlight, return: normalizedReturnFlight },
        route: "domestic-flight",
    };

    return (
        <Container maxWidth="xl">
            <Grid className="passengers" container item xs={12} rowGap={3} mb={forms?.length > 1 ? 30 : 26}>
                <MobileHeader title={t("passengersInformation")} justifyContent="center" handleClick={navigateBack} />
                {!!normalizedDepartureFlight?.departureTime && ( //avoid showing selected item on the flight automatic reserve page
                    <Grid container item xs={12} rowGap={3} columnGap={1} mt={IS_MOBILE ? 7 : 0}>
                        <SelectedItem data={data} />
                    </Grid>
                )}
                <Grid component="form" container item xs={12} rowGap={3} onSubmit={handleSubmit(handleRegistration, handleError)}>
                    {IS_MOBILE ? (
                        <Grid className="passengers__forms" container item xs={12}>
                            {forms.map((form, formIndex) => {
                                return (
                                    <TabPanel value={tabValue} index={formIndex} key={form?.id}>
                                        <PassengersFormContainer
                                            formIndex={formIndex}
                                            formType="flight"
                                            passengerType={t(`${passengerTypes()[formIndex]}`)}
                                            forms={forms}
                                            form={form}
                                            formCount={passengerTypes()}
                                            IDcard={IDcard}
                                            setIDcard={setIDcard}
                                            {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
                                        />
                                    </TabPanel>
                                );
                            })}

                            {forms?.length > 1 ? (
                                <BaseTabPanel
                                    list={forms}
                                    labels={passengerTypes()}
                                    tabValue={tabValue}
                                    handleTabChange={handleTabChange}
                                    isError={errors.passengers?.[tabValue]}
                                />
                            ) : null}
                        </Grid>
                    ) : (
                        <>
                            <BaseTitle
                                title={
                                    <Grid container item xs={12} columnGap={1} justifyContent="space-between" alignItems="center">
                                        <Icon name="PassengerIcon" /> {t("passengersInformation")}
                                    </Grid>
                                }
                                dividerColor="1px solid var(--medium-gray-color)"
                            />
                            <Grid className="passengers__forms" container item xs={12} rowGap={5}>
                                {forms.map((form, formIndex) => {
                                    return (
                                        <Fragment key={form.id}>
                                            <PassengersFormContainer
                                                formIndex={formIndex}
                                                formType="flight"
                                                passengerType={t(`${passengerTypes()[formIndex]}`)}
                                                form={form}
                                                formCount={passengerTypes()}
                                                IDcard={IDcard}
                                                setIDcard={setIDcard}
                                                {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
                                            />
                                            {formIndex < forms.length - 1 && <Divider width="100%" />}
                                        </Fragment>
                                    );
                                })}
                            </Grid>
                        </>
                    )}
                    <Grid
                        className="passengers__submit-information"
                        container
                        item
                        xs={12}
                        rowGap={2}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <RulesLink />
                        <Grid container item xs={12} md={2} justifyContent="flex-end">
                            <BaseButton text={t("submitInformation")} color="primary" size="large" radius="radius-10" type="submit" />
                        </Grid>
                    </Grid>
                </Grid>

                <OldPassengersModal open={isPassengersModalOpen} setOpen={setIsPassengersModalOpen} selectedOldPassenger={handleSelectPassenger} />
            </Grid>
        </Container>
    );
});
